import {
  AmountDto,
  ApplicationLocalizationDto,
  CurrentCultureDto
} from '@unistocks/proxy/axios-client';
import { i18n } from '@unistocks/shared/localization/i18n';
import {
  mapResourcesToI18nDict,
  mergeResourcesWithBaseResource
} from '@unistocks/shared/localization/utils';
import { getDeviceLocale } from '@unistocks/shared/utils/device-utils';
import { createDate, isNumber, isString } from '@unistocks/shared/utils/util';
import { TranslateOptions } from 'i18n-js';
import { DateTime, Settings as luxonSettings } from 'luxon';

declare module 'luxon' {
  interface TSSettings {
    throwOnInvalid: true;
  }
}

type LocaleOption = { locale?: Intl.LocalesArgument };

luxonSettings.defaultLocale = getDeviceLocale();
luxonSettings.throwOnInvalid = true;

luxonSettings.defaultWeekSettings = {
  firstDay: 1,
  weekend: [6, 7],
  minimalDays: 4
};
let _resourceName: string, _currentLocale: string;

export const updateLocalization = (
  currentCulture: CurrentCultureDto,
  defaultResourceName: string,
  resources: ApplicationLocalizationDto['resources']
) => {
  _resourceName = defaultResourceName;

  if (currentCulture.cultureName) {
    _currentLocale = i18n.locale = luxonSettings.defaultLocale = currentCulture.cultureName;

    const mergedRecources = mergeResourcesWithBaseResource(resources);
    const translations = mapResourcesToI18nDict(mergedRecources);

    i18n.store({
      [currentCulture.cultureName]: translations
    });
  }
};

export function t(key: string, args?: TranslateOptions): string {
  if (key.slice(0, 2) === '::') {
    key = _resourceName + key;
  }

  return i18n.t(key, args);
}

export function l(
  value: number,
  scope: 'currency' | 'number' | 'percentage',
  options: Intl.NumberFormatOptions & { locale?: Intl.LocalesArgument } = {}
) {
  'worklet';
  if (!isNumber(value)) return '';

  let formatter: Intl.NumberFormat;
  const locale = options.locale ?? _currentLocale;

  switch (scope) {
    case 'currency':
      formatter = new Intl.NumberFormat(locale, {
        // TODO: some defaults not supported by react-native yet
        currencyDisplay: 'narrowSymbol',
        notation: 'standard',
        minimumFractionDigits: 0,
        ...options,
        style: 'currency'
      });
      break;

    case 'number':
      formatter = new Intl.NumberFormat(locale, {
        minimumIntegerDigits: 1,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
        ...options,
        style: 'decimal'
      });
      break;

    case 'percentage':
      formatter = new Intl.NumberFormat(locale, {
        minimumIntegerDigits: 1,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
        ...options,
        style: 'percent'
      });
      break;
  }

  return formatter.format(value);
}

type FormatCurrencyOptions = Omit<Intl.NumberFormatOptions, 'currency' | 'style'> & LocaleOption;

export function formatCurrency(amount: AmountDto, options?: FormatCurrencyOptions): string;
export function formatCurrency(
  value: number,
  currency: string,
  options?: FormatCurrencyOptions
): string;
export function formatCurrency(
  valueOrAmount: number | AmountDto,
  currencyOrOptions?: string | FormatCurrencyOptions,
  optionsOrUndefined?: FormatCurrencyOptions
): string {
  'worklet';
  let value: number, currency: string, options: FormatCurrencyOptions;

  if (isNumber(valueOrAmount)) {
    if (!isString(currencyOrOptions)) throw new Error('Currency is required');

    value = valueOrAmount;
    currency = currencyOrOptions as string;
    options = optionsOrUndefined as FormatCurrencyOptions;
  } else {
    value = valueOrAmount.value;
    currency = valueOrAmount.currency;
    options = currencyOrOptions as FormatCurrencyOptions;
  }

  return l(value, 'currency', { currency, ...options });
}

export function formatNumber(
  value: number,
  options: Omit<Intl.NumberFormatOptions, 'style'> & LocaleOption = {}
): string {
  'worklet';
  return l(value, 'number', options);
}

export function formatPercentage(
  value: number,
  options: Omit<Intl.NumberFormatOptions, 'style'> & LocaleOption = {}
): string {
  'worklet';
  return l(value, 'percentage', options);
}

/**
 * Format date using Intl.DateTimeFormat. Removes time part of the date.
 * @param value
 * @param options
 * @returns
 */
export function formatDate(
  value: string | number | Date | DateTime,
  options: Intl.DateTimeFormatOptions & LocaleOption = {}
): string {
  if (value instanceof DateTime) value = value.toJSDate();

  if (typeof value === 'string') value = createDate(value).toJSDate();

  return new Intl.DateTimeFormat(options.locale ?? _currentLocale, options).format(value);
}

/**
 * Format date using Intl.DateTimeFormat. Removes time part of the date.
 * Luxon not available in worklets
 * @param value
 * @param options
 * @returns
 */
export function formatDateWorklet(
  value: number | string | Date,
  options: Intl.DateTimeFormatOptions & LocaleOption = {}
): string {
  'worklet';
  if (typeof value === 'string') value = new Date(value);
  return new Intl.DateTimeFormat(options.locale ?? _currentLocale, options).format(value);
}

/**
 * Format date and time using Intl.DateTimeFormat.
 * @param value
 * @param options
 * @returns
 */
export function formatDateTime(
  value: string | number | Date | DateTime,
  options: Intl.DateTimeFormatOptions & LocaleOption = {
    dateStyle: 'short',
    timeStyle: 'short'
  }
): string {
  if (value instanceof DateTime) value = value.toJSDate();

  if (typeof value === 'string') value = DateTime.fromISO(value).toJSDate();

  return new Intl.DateTimeFormat(options.locale ?? _currentLocale, options).format(value);
}
